<template>
  <Card class="layout-card" style="height: 100%">
    <div class="activity-page">
      <Tabs v-model="activeTab" @on-click="handleTabChange">
        <TabPane
          :label="`日常任务(${tabTotals[TASK_CATEGORY_DAILY] || 0})`"
          :name="String(TASK_CATEGORY_DAILY)"
        />
        <TabPane
          :label="`新用户任务(${tabTotals[TASK_CATEGORY_NEW_USER] || 0})`"
          :name="String(TASK_CATEGORY_NEW_USER)"
        />
      </Tabs>

      <div class="toolbar-row">
        <div class="search-form">
          <Select
            v-model="searchForm.activityType"
            clearable
            filterable
            placeholder="任务类型"
            class="toolbar-item"
          >
            <Option
              v-for="item in currentTaskTypeOptions"
              :key="item.value"
              :value="item.value"
              :label="item.label"
            >
              {{ item.label }}
            </Option>
          </Select>

          <Select
            v-model="searchForm.crowdIds"
            multiple
            clearable
            filterable
            :max-tag-count="1"
            placeholder="选择人群包"
            class="toolbar-item"
          >
            <Option
              v-for="item in crowdOptions"
              :key="item.id"
              :value="item.id"
              :label="item.name"
            >
              {{ item.name }}
            </Option>
          </Select>

          <Input
            v-model.trim="searchForm.activityName"
            clearable
            placeholder="请输入任务名称"
            class="toolbar-item toolbar-item--wide"
          />

          <Select
            v-model="searchForm.activityStatus"
            clearable
            placeholder="状态"
            class="toolbar-item"
          >
            <Option
              v-for="item in activityStatusOptions"
              :key="item.value"
              :value="item.value"
              :label="item.label"
            >
              {{ item.label }}
            </Option>
          </Select>

          <Button type="primary" @click="getData(1)">搜索</Button>
          <Button @click="handleReset">重置</Button>
        </div>

        <Button type="primary" @click="handleOpenEditModal()">新增</Button>
      </div>

      <div class="table-wrap">
        <Table :data="tableData" :columns="columns" :loading="loading">
          <template #activityType="{ row }">
            <span>{{ getTaskTypeLabel(row.activityType) }}</span>
          </template>

          <template #crowdDisplay="{ row }">
            <span>{{ getCrowdDisplayText(row) }}</span>
          </template>

          <template #availableLimit="{ row }">
            <span>{{ getAvailableLimitLabel(row.availableLimit) }}</span>
          </template>

          <template #activityStatus="{ row }">
            <i-switch
              :value="Number(row.activityStatus)"
              :true-value="1"
              :false-value="0"
              :disabled="loading || Boolean(statusChangingId)"
              :loading="statusChangingId === row.id"
              @on-change="(value) => syncActivityStatusChange(row, value)"
            />
          </template>

          <template #action="{ row }">
            <div class="action-links">
              <a @click="handleOpenEditModal(row)">编辑</a>
              <a
                v-if="Number(row.activityType) === 3"
                @click="handleOpenLinkModal(row)"
              >
                链接配置
              </a>
              <a class="link-danger" @click="handleDelete(row)">删除</a>
            </div>
          </template>

          <template #footer>
            <Pages :page="page" :func="getData" />
          </template>
        </Table>
      </div>
    </div>

    <TaskModal
      v-model="editVisible"
      :data="editForm"
      :crowd-options="crowdOptions"
      :task-category="editTaskCategory"
      @on-success="handleSaveSuccess"
    />

    <Modal
      v-model="linkVisible"
      title="YouTube链接配置"
      :width="800"
      :mask-closable="false"
    >
      <Form :label-width="100">
        <FormItem
          v-for="(item, index) in linkList"
          :key="index"
          :label="item.chineseLangName"
        >
          <div class="link-config-row">
            <Input :disabled="!item.editable" v-model="item.youtubeUrl" />
            <a @click="$set(item, 'editable', true)">编辑</a>
          </div>
        </FormItem>
      </Form>
      <template #footer>
        <Button @click="linkVisible = false">取消</Button>
        <Button type="primary" :loading="linkLoading" @click="handleSubmitLink">
          提交
        </Button>
      </template>
    </Modal>
  </Card>
</template>

<script>
import Pages from '@/components/pages'
import {
  changeActivityStatus,
  delActivity,
  getActivityConfig,
  getActivityInfo,
  getActivityList,
  saveLinkConfig
} from '@/api/activity'
import { getCrowdList } from '@/api/crowd'
import TaskModal from './components/TaskModal.vue'
import {
  ACTIVITY_STATUS_OPTIONS,
  TASK_CATEGORY_DAILY,
  TASK_CATEGORY_NEW_USER,
  getAvailableLimitLabel,
  getTaskTypeLabel,
  getTaskTypeOptionsByCategory,
  inferTaskCategoryByType
} from './constants'

const createSearchForm = () => ({
  activityName: '',
  activityType: '',
  crowdIds: [],
  activityStatus: ''
})

export default {
  name: 'ActivityTaskList',
  components: {
    Pages,
    TaskModal
  },
  data() {
    return {
      TASK_CATEGORY_DAILY,
      TASK_CATEGORY_NEW_USER,
      activeTab: String(TASK_CATEGORY_DAILY),
      activityStatusOptions: ACTIVITY_STATUS_OPTIONS,
      searchForm: createSearchForm(),
      loading: false,
      statusChangingId: '',
      listRequestSerial: 0,
      page: {
        current: 1,
        size: 10,
        total: 0
      },
      tableData: [],
      crowdOptions: [],
      tabTotals: {
        [TASK_CATEGORY_DAILY]: 0,
        [TASK_CATEGORY_NEW_USER]: 0
      },
      editVisible: false,
      editForm: {},
      editTaskCategory: TASK_CATEGORY_DAILY,
      linkVisible: false,
      linkLoading: false,
      currentLinkActivityId: '',
      linkList: [],
      columns: [
        {
          title: '任务名称',
          key: 'activityName',
          minWidth: 160
        },
        {
          title: '任务类型',
          slot: 'activityType',
          minWidth: 120
        },
        {
          title: '展示人群',
          slot: 'crowdDisplay',
          minWidth: 160
        },
        {
          title: '奖励金币',
          key: 'bonusCoins',
          minWidth: 100
        },
        {
          title: '可完成次数',
          slot: 'availableLimit',
          minWidth: 110
        },
        {
          title: '排序值',
          key: 'activitySort',
          minWidth: 90
        },
        {
          title: '状态',
          slot: 'activityStatus',
          minWidth: 80,
          align: 'center'
        },
        {
          title: '操作',
          slot: 'action',
          minWidth: 220,
          align: 'center'
        }
      ]
    }
  },
  computed: {
    currentTaskCategory() {
      return Number(this.activeTab)
    },
    currentTaskTypeOptions() {
      return getTaskTypeOptionsByCategory(this.currentTaskCategory)
    },
    crowdNameMap() {
      return this.crowdOptions.reduce((acc, item) => {
        acc[String(item.id)] = item.name
        return acc
      }, {})
    }
  },
  methods: {
    getTaskTypeLabel,
    getAvailableLimitLabel,
    normalizeTableData(records) {
      if (!Array.isArray(records)) return []

      return records.map((item) => ({
        ...item,
        activityStatus:
          item.activityStatus === '' ||
          item.activityStatus === null ||
          item.activityStatus === undefined
            ? item.activityStatus
            : Number(item.activityStatus)
      }))
    },
    buildSearchParams() {
      const params = {
        current: this.page.current,
        size: this.page.size,
        taskCategory: this.currentTaskCategory,
        activityName: this.searchForm.activityName || undefined,
        activityType:
          this.searchForm.activityType === 0 || this.searchForm.activityType
            ? Number(this.searchForm.activityType)
            : undefined,
        activityStatus:
          this.searchForm.activityStatus === 0 || this.searchForm.activityStatus
            ? Number(this.searchForm.activityStatus)
            : undefined
      }

      if (
        Array.isArray(this.searchForm.crowdIds) &&
        this.searchForm.crowdIds.length
      ) {
        params.crowdIds = [...this.searchForm.crowdIds]
      }

      return params
    },
    getCrowdDisplayText(row) {
      if (Number(row.crowdLimit) === 0) return '不限'
      const crowdIds = Array.isArray(row.crowdIds) ? row.crowdIds : []
      if (!crowdIds.length) return '不限'
      const names = crowdIds
        .map((id) => this.crowdNameMap[String(id)] || id)
        .filter(Boolean)
      return names.length ? names.join('、') : '不限'
    },
    async refreshTabTotals() {
      try {
        const [dailyRes, newUserRes] = await Promise.all([
          getActivityList({
            current: 1,
            size: 1,
            taskCategory: TASK_CATEGORY_DAILY
          }),
          getActivityList({
            current: 1,
            size: 1,
            taskCategory: TASK_CATEGORY_NEW_USER
          })
        ])
        this.tabTotals = {
          [TASK_CATEGORY_DAILY]: dailyRes.data.total || 0,
          [TASK_CATEGORY_NEW_USER]: newUserRes.data.total || 0
        }
      } catch (error) {}
    },
    async fetchCrowdOptions() {
      try {
        const { data } = await getCrowdList({
          current: -1,
          size: -1
        })
        this.crowdOptions = data.records || []
      } catch (error) {
        this.$Message.error('获取人群包列表失败')
      }
    },
    async getData(page) {
      if (page) this.page.current = page
      else this.page.current = 1

      const requestSerial = ++this.listRequestSerial
      this.loading = true
      try {
        const { data } = await getActivityList(this.buildSearchParams())
        if (requestSerial !== this.listRequestSerial) return
        this.tableData = this.normalizeTableData(data.records)
        this.page.total = data.total || 0
        this.page.current = data.current || this.page.current
      } catch (error) {
        this.$Message.error('获取任务列表失败')
      } finally {
        if (requestSerial === this.listRequestSerial) {
          this.loading = false
        }
      }
    },
    handleTabChange(name) {
      this.activeTab = name
      this.searchForm = createSearchForm()
      this.page.current = 1
      this.getData()
    },
    handleReset() {
      this.searchForm = createSearchForm()
      this.getData(1)
    },
    async handleOpenEditModal(row) {
      if (!row || !row.id) {
        this.editTaskCategory = this.currentTaskCategory
        this.editForm = {}
        this.editVisible = true
        return
      }

      this.loading = true
      try {
        const { data } = await getActivityInfo(row.id)
        this.editTaskCategory = inferTaskCategoryByType(
          data.activityType,
          Number(data.taskCategory) || this.currentTaskCategory
        )
        this.editForm = data || {}
        this.editVisible = true
      } catch (error) {
        this.$Message.error('获取任务详情失败')
      } finally {
        this.loading = false
      }
    },
    handleSaveSuccess() {
      this.getData(this.page.current)
      this.refreshTabTotals()
    },
    async syncActivityStatusChange(row, value) {
      if (this.loading || this.statusChangingId) return

      const previousStatus = Number(row.activityStatus)
      const nextStatus = Number(value)

      if (previousStatus === nextStatus) return

      this.statusChangingId = row.id
      this.$set(row, 'activityStatus', nextStatus)

      try {
        await changeActivityStatus(row.id, nextStatus)
        this.$Message.success('状态修改成功')
        this.getData(this.page.current)
      } catch (error) {
        this.$set(row, 'activityStatus', previousStatus)
        this.$Message.error('状态修改失败')
      } finally {
        this.statusChangingId = ''
      }
    },
    async handleStatusChange(row, value) {
      if (this.loading || this.statusChangingId) return

      const previousStatus = Number(row.activityStatus)
      const nextStatus = Number(value)

      if (previousStatus === nextStatus) return
      try {
        this.statusChangingId = row.id
        this.$set(row, 'activityStatus', nextStatus)
        await changeActivityStatus(row.id, nextStatus)
        this.getData(this.page.current)
        this.$Message.success('状态修改成功')
      } catch (error) {
        this.$Message.error('状态修改失败')
      } finally {
        this.loading = false
      }
    },
    handleDelete(row) {
      this.$Modal.confirm({
        title: '提示',
        content: `是否确定删除任务 "${row.activityName}"?`,
        onOk: async () => {
          try {
            await delActivity(row.id)
            this.$Message.success('删除成功')
            this.getData(this.page.current)
            this.refreshTabTotals()
          } catch (error) {
            this.$Message.error('删除失败')
          }
        }
      })
    },
    async handleOpenLinkModal(row) {
      this.currentLinkActivityId = row.id
      try {
        const { data } = await getActivityConfig(row.id)
        this.linkList = (data || []).map((item) => ({
          ...item,
          editable: Boolean(item.editable)
        }))
        this.linkVisible = true
      } catch (error) {
        this.$Message.error('获取链接配置失败')
      }
    },
    async handleSubmitLink() {
      this.linkLoading = true
      try {
        await saveLinkConfig(
          {
            activityId: this.currentLinkActivityId,
            activityLinkConfigDetailDTOList: this.linkList
          },
          this.currentLinkActivityId
        )
        this.$Message.success('保存成功')
        this.linkVisible = false
      } catch (error) {
        this.$Message.error('保存失败')
      } finally {
        this.linkLoading = false
      }
    }
  },
  mounted() {
    this.fetchCrowdOptions()
    this.getData()
    this.refreshTabTotals()
  }
}
</script>

<style scoped lang="less">
.layout-card {
  /deep/ .ivu-card-body {
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
}

.activity-page {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.toolbar-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
}

.search-form {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.toolbar-item {
  width: 160px;
}

.toolbar-item--wide {
  width: 220px;
}

.table-wrap {
  flex: 1;
  min-height: 0;
  overflow: auto;
}

.action-links {
  display: flex;
  justify-content: center;
  gap: 12px;
}

.link-danger {
  color: #ed4014;
}

.link-config-row {
  display: flex;
  align-items: center;
  gap: 10px;
}
</style>
